import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from 'styled-components'

import Link from 'components/Link'
import Modal from 'components/Modal'
import WeChatContactModal from 'components/WeChatContactModal'
import { Title26, Label, Body14 } from 'components/Typography'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'
import theme from 'styles/theme'

import { Wrapper, ServicesWrapper, Logo } from './BrandList.style'

const BrandList = ({
  brands,
  villageName,
  contactIcons,
  visitBoutiqueLabel,
  showContactMethods = false,
  headline,
}) => {
  const weChatModalRef = useRef()
  const [weChatImage, setWeChatImage] = useState(null)
  const [weChatID, setWeChatID] = useState(null)

  const { colors } = useTheme()
  return (
    <Wrapper gridTemplateColumns={['1fr', '1fr 1fr', '1fr 1fr 1fr 1fr']}>
      {weChatID ? (
        <Modal ref={weChatModalRef}>
          <WeChatContactModal weChatImage={weChatImage} weChatID={weChatID} />
        </Modal>
      ) : null}
      {brands.map((item, index) => {
        const virtualShoppingDetails = item.content?.find(
          (c) => c.__typename === 'ContentfulCompVirtualShoppingDetailsVsd01'
        )
        const methods = []

        if (virtualShoppingDetails) {
          if (virtualShoppingDetails.whatsapp) {
            methods.push({
              trackLabel: 'WhatsApp Number',
              icon: contactIcons.whatsappIcon,
              label: contactIcons.whatsappLabel,
              link: `https://wa.me/${virtualShoppingDetails.whatsapp.replace(
                /\s/g,
                ''
              )}`,
              showExternalIcon: false,
            })
          }

          if (virtualShoppingDetails.productCatalogue) {
            methods.push({
              trackLabel: 'Product Catalogue URL',
              icon: contactIcons.productCatalogueIcon,
              label: contactIcons.productCatalogueLabel,
              link: virtualShoppingDetails.productCatalogue,
              showExternalIcon: false,
            })
          }

          if (virtualShoppingDetails.bookingUrl) {
            methods.push({
              trackLabel: 'Booking URL',
              icon: contactIcons.bookingUrlIcon,
              label: contactIcons.bookingUrlLabel,
              link: virtualShoppingDetails.bookingUrl,
              showExternalIcon: false,
            })
          }

          if (virtualShoppingDetails.virtualShoppingEmail) {
            methods.push({
              trackLabel: 'Virtual Shopping Email',
              icon: contactIcons.emailAddressIcon,
              label: contactIcons.emailAddressLabel,
              link: `mailto:${virtualShoppingDetails.virtualShoppingEmail}`,
              showExternalIcon: false,
            })
          }

          if (virtualShoppingDetails.virtualShoppingPhone) {
            methods.push({
              trackLabel: 'Virtual Shopping Phone',
              icon: contactIcons.phoneNumberIcon,
              label: contactIcons.phoneNumberLabel,
              link: `tel:${virtualShoppingDetails.virtualShoppingPhone}`,
              showExternalIcon: false,
            })
          }

          if (virtualShoppingDetails.weChatID) {
            methods.push({
              trackLabel: 'weChat ID',
              icon: contactIcons.weChatIcon,
              label: contactIcons.weChatLabel,
              link: null,
              showExternalIcon: false,
              onClickCallback: () => weChatModalRef?.current?.openModal(),
            })
          }
        }

        return (
          <Title26
            key={`${item.name}_${index.toString()}`}
            as="li"
            fontSize="5">
            <Link
              onClick={() => {
                if (methods) {
                  trackGTM(
                    'Virtual Shopping',
                    'Virtual Shopping',
                    'brand click',
                    item.name
                  )
                  GA4('brand_click', {
                    brand_name: item.name,
                    village_name: villageName,
                  })
                } else {
                  trackGTM('Brands', 'brands', 'brand click', item.name)
                  GA4('brand_click', {
                    brand_name: item.name,
                    village_name: villageName,
                  })
                }
              }}
              to={item.link}>
              {item.name}
            </Link>
            {item.statusLabel && (
              <Label
                backgroundColor={colors.sagemediumToGreen}
                color={colors.sagedarkToSandstone}>
                {item.statusLabel}
              </Label>
            )}
            {showContactMethods ? (
              <ServicesWrapper>
                {methods &&
                  methods.map((service) => {
                    const iconProps = {
                      display: ['block', null, null],
                      dangerouslySetInnerHTML: { __html: service.icon },
                      paddingRight: theme.space[1],
                    }

                    return (
                      <Logo
                        as="a"
                        href={service.link}
                        {...iconProps}
                        onClick={() => {
                          trackGTM(
                            'Virtual Shopping',
                            'Virtual Shopping',
                            item.name,
                            service.trackLabel
                          )
                          GA4('virtual_shopping_contact_click', {
                            village_name: villageName,
                            brand_name: item.name,
                            component_location: headline,
                            contact_type: service.trackLabel,
                          })
                          if (service.onClickCallback) {
                            setWeChatImage(virtualShoppingDetails.weChatImage)
                            setWeChatID(virtualShoppingDetails.weChatID)
                            service.onClickCallback()
                          }
                        }}
                      />
                    )
                  })}
              </ServicesWrapper>
            ) : null}
            {showContactMethods && methods && methods.length > 0 && (
              <Body14
                id="brand-list--link"
                as={Link}
                pb={theme.space[4]}
                to={item.link}
                onClick={() => {
                  trackGTM(
                    'Virtual Shopping',
                    'Virtual Shopping',
                    'brand click',
                    item.name
                  )
                }}>
                {visitBoutiqueLabel}
              </Body14>
            )}
          </Title26>
        )
      })}
    </Wrapper>
  )
}

BrandList.propTypes = {
  brands: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  villageName: PropTypes.string,
  contactIcons: PropTypes.shape({
    whatsappIcon: PropTypes.string,
    productCatalogueIcon: PropTypes.string,
    bookingUrlIcon: PropTypes.string,
    emailAddressIcon: PropTypes.string,
    phoneNumberIcon: PropTypes.string,
    weChatIcon: PropTypes.string,
    whatsappLabel: PropTypes.string,
    emailAddressLabel: PropTypes.string,
    productCatalogueLabel: PropTypes.string,
    phoneNumberLabel: PropTypes.string,
    bookingUrlLabel: PropTypes.string,
    weChatLabel: PropTypes.string,
  }),
  visitBoutiqueLabel: PropTypes.string,
  showContactMethods: PropTypes.bool,
  headline: PropTypes.string,
}

BrandList.defaultProps = {
  contactIcons: {
    whatsappIcon: '',
    title: '',
    productCatalogueIcon: '',
    bookingUrlIcon: '',
    emailAddressIcon: '',
    phoneNumberIcon: '',
    weChatIcon: '',
    whatsappLabel: '',
    emailAddressLabel: '',
    productCatalogueLabel: '',
    phoneNumberLabel: '',
    bookingUrlLabel: '',
    weChatLabel: '',
  },
  villageName: '',
  visitBoutiqueLabel: 'VISIT THE BOUTIQUE',
  showContactMethods: false,
  headline: '',
}

export default BrandList
